import CryptoJS from 'crypto-js';
export const generatePubKeyPem = async (pubKey)=>{
    const { subtle } = window.crypto;
    // Export to raw SPKI DER buffer
    const spkiBuffer = await subtle.exportKey('spki', pubKey);
    // ArrayBuffer -> Base64 conversion
    const bytes = new Uint8Array(spkiBuffer);
    let binary = '';
    for(let i = 0; i < bytes.length; i++){
        binary += String.fromCharCode(bytes[i]);
    }
    const base64 = btoa(binary);
    // Splitting a Base64 string into lines of 64 characters (PEM standard)
    const pemLines = base64.match(/.{1,64}/g) || [];
    return [
        '-----BEGIN PUBLIC KEY-----',
        ...pemLines,
        '-----END PUBLIC KEY-----'
    ].join('\n');
};
//----------------------------------------------------------------------------------------------------------------------
const PBKDF2_ITERATIONS = 600000;
const wrapEncryptedPkcs8Der = (encryptedData, salt, iv, iterations)=>{
    const encodeLength = (len)=>{
        if (len < 128) return [
            len
        ];
        if (len < 256) return [
            0x81,
            len
        ];
        return [
            0x82,
            len >> 8 & 0xff,
            len & 0xff
        ];
    };
    const createNode = (tag, payload)=>{
        const lenBytes = encodeLength(payload.length);
        const result = new Uint8Array(1 + lenBytes.length + payload.length);
        result[0] = tag;
        result.set(lenBytes, 1);
        result.set(payload, 1 + lenBytes.length);
        return result;
    };
    const encodeInteger = (value)=>{
        const bytes = [];
        let temp = value;
        while(temp > 0){
            bytes.unshift(temp & 0xff);
            temp = Math.floor(temp / 256);
        }
        if (bytes.length === 0) bytes.push(0);
        if (bytes[0] & 0x80) bytes.unshift(0x00);
        return createNode(0x02, bytes);
    };
    const concat = (...arrays)=>{
        const totalLen = arrays.reduce((acc, curr)=>acc + curr.length, 0);
        const result = new Uint8Array(totalLen);
        let offset = 0;
        for (const arr of arrays){
            result.set(arr, offset);
            offset += arr.length;
        }
        return result;
    };
    // 1. OIDs ASN.1 (Tag 0x06)
    const oidPBES2 = createNode(0x06, [
        0x2a,
        0x86,
        0x48,
        0x86,
        0xf7,
        0x0d,
        0x01,
        0x05,
        0x0d
    ]); // 1.2.840.113549.1.5.13
    const oidPBKDF2 = createNode(0x06, [
        0x2a,
        0x86,
        0x48,
        0x86,
        0xf7,
        0x0d,
        0x01,
        0x05,
        0x0c
    ]); // 1.2.840.113549.1.5.12
    const oidAES256CBC = createNode(0x06, [
        0x60,
        0x86,
        0x48,
        0x01,
        0x65,
        0x03,
        0x04,
        0x01,
        0x2a
    ]); // 2.16.840.1.101.3.4.1.42
    const oidHmacSHA256 = createNode(0x06, [
        0x2a,
        0x86,
        0x48,
        0x86,
        0xf7,
        0x0d,
        0x02,
        0x09
    ]); // 1.2.840.113549.2.9
    // 2. PBKDF2 Params
    const saltNode = createNode(0x04, salt); // OCTET STRING
    const iterNode = encodeInteger(iterations); // INTEGER
    const nullNode = createNode(0x05, []);
    const prfSeq = createNode(0x30, concat(oidHmacSHA256, nullNode)); // AlgorithmIdentifier (hmacWithSHA256 + NULL)
    const pbkdf2Params = createNode(0x30, concat(saltNode, iterNode, prfSeq));
    const pbkdf2Alg = createNode(0x30, concat(oidPBKDF2, pbkdf2Params));
    // 3. AES-256-CBC Params
    const ivNode = createNode(0x04, iv); // OCTET STRING
    const aesAlg = createNode(0x30, concat(oidAES256CBC, ivNode));
    // 4. PBES2 Params & Main AlgorithmIdentifier
    const pbes2Params = createNode(0x30, concat(pbkdf2Alg, aesAlg));
    const pbes2Alg = createNode(0x30, concat(oidPBES2, pbes2Params));
    // 5. EncryptedData OCTET STRING
    const encDataNode = createNode(0x04, encryptedData);
    // 6. PKCS#8 Master Sequence
    return createNode(0x30, concat(pbes2Alg, encDataNode));
};
export const generatePrvKeyPem = async (prvKey, passphrase)=>{
    const { subtle } = window.crypto;
    // 1. Export to raw PKCS#8 DER buffer
    const pkcs8Buffer = await subtle.exportKey('pkcs8', prvKey);
    // 2. Passphrase – we encrypt data using PBKDF2 + AES-256-CBC
    const salt = window.crypto.getRandomValues(new Uint8Array(16)); // 128-bit salt
    const iv = window.crypto.getRandomValues(new Uint8Array(16)); // 128-bit IV
    // // AES-256 key generation (256-bit = 8 words) using PBKDF2
    const encoder = new TextEncoder();
    const passphraseKey = await subtle.importKey('raw', encoder.encode(passphrase), {
        name: 'PBKDF2'
    }, false, [
        'deriveKey'
    ]);
    const derivedCryptoKey = await subtle.deriveKey({
        name: 'PBKDF2',
        salt,
        iterations: PBKDF2_ITERATIONS,
        hash: 'SHA-256'
    }, passphraseKey, {
        name: 'AES-CBC',
        length: 256
    }, true, [
        'encrypt'
    ]);
    // Exporting the designated key to CryptoJS WordArray format
    const derivedKeyBuffer = await subtle.exportKey('raw', derivedCryptoKey);
    const derivedKeyHex = Array.from(new Uint8Array(derivedKeyBuffer)).map((b)=>b.toString(16).padStart(2, '0')).join('');
    const derivedKey = CryptoJS.enc.Hex.parse(derivedKeyHex);
    const ivWordArray = CryptoJS.enc.Hex.parse(Array.from(iv).map((b)=>b.toString(16).padStart(2, '0')).join(''));
    // PKCS#8 to WordArray conversion for CryptoJS
    const pkcs8Hex = Array.from(new Uint8Array(pkcs8Buffer)).map((b)=>b.toString(16).padStart(2, '0')).join('');
    const pkcs8WordArray = CryptoJS.enc.Hex.parse(pkcs8Hex);
    // AES-256-CBC encryption
    const encrypted = CryptoJS.AES.encrypt(pkcs8WordArray, derivedKey, {
        iv: ivWordArray,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
    });
    // 3. Building a PKCS#8 EncryptedPrivateKeyInfo structure in ASN.1 (with salt and IV included)
    const encHex = encrypted.ciphertext.toString(CryptoJS.enc.Hex);
    const encBytes = new Uint8Array(encHex.match(/.{1,2}/g).map((byte)=>parseInt(byte, 16)));
    // We use universal, clean ASN.1 DER wrapping (PBES2 / AES-256-CBC)
    const asn1Der = wrapEncryptedPkcs8Der(encBytes, salt, iv, PBKDF2_ITERATIONS);
    let binary = '';
    for(let i = 0; i < asn1Der.length; i++){
        binary += String.fromCharCode(asn1Der[i]);
    }
    const base64 = btoa(binary);
    const pemLines = base64.match(/.{1,64}/g) || [];
    return [
        '-----BEGIN ENCRYPTED PRIVATE KEY-----',
        ...pemLines,
        '-----END ENCRYPTED PRIVATE KEY-----'
    ].join('\n');
};
